import { useState } from 'react';
import { css } from '@emotion/react';
import {
  BookableEntity,
  ScheduleDefinition,
  SDBookableAppointmentType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import {
  Table,
  TableColumnConfig,
  EditIcon,
  Chip,
  Truncated,
  CopyIcon,
  TrashIcon,
  ChipVariants,
  useModalControl,
} from '@frontend/design-system';
import { AvailabilityMode } from '../../../../components/AvailabilityRules';
import { useGetAvailabilityRules } from '../../hooks/use-get-availability-rules';
import { parseRRule } from '../../utils';
import { DeleteConfirmationModal } from '../delete-confirmation-modal';
import { DuplicateConfirmationModal } from '../duplicate-confirmation-modal';

type Props = {
  availabilityMode: AvailabilityMode;
  data: ScheduleDefinition[];
  locationId: string;
  selectedScheduleDefinition: ScheduleDefinition | null;
  openAddEditTray: (scheduleDefinition: ScheduleDefinition | null, resourceId: string, isCopy?: boolean) => void;
  entityId: string;
};

export const AvailabilityTable = ({
  availabilityMode,
  data,
  locationId,
  selectedScheduleDefinition,
  openAddEditTray,
  entityId,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const [tempScheduleDefinition, setTempScheduleDefinition] = useState<ScheduleDefinition | null>(null);
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();
  const { modalProps: duplicateModalProps, triggerProps: duplicateTriggerProps } = useModalControl();

  const { data: availabilityRulesData } = useGetAvailabilityRules({
    locationId,
    searchValue: '',
    availabilityMode,
  });

  const resourceOptions = (availabilityRulesData || []).map((rule) => ({
    id: rule.id,
    name: rule.displayName || rule.externalName,
  }));

  const colConfig: TableColumnConfig<ScheduleDefinition>[] = [
    {
      id: 'status',
      Header: t('Status'),
      accessor: 'rRule', // To be replaced with actual status field when available in schema
      disableSortBy: true,
      width: 80,
      cellRenderer: (cellValue: string) => {
        let variant = 'critical' as ChipVariants;
        let iconName = 'remove' as IconName;

        switch (cellValue) {
          case 'ACTIVE':
            variant = 'success';
            iconName = 'check';
            break;
          case 'PENDING':
            variant = 'warn';
            iconName = 'pending';
            break;
          case 'INACTIVE':
            variant = 'neutral';
            iconName = 'block';
            break;
          case 'ERROR':
            iconName = 'remove';
            variant = 'critical';
            break;
          default:
            break;
        }

        return (
          <Chip variant={variant}>
            <Icon name={iconName} size={16} />
          </Chip>
        );
      },
    },
    {
      id: 'recurrence',
      Header: t('Recurrence'),
      accessor: 'rRule',
      disableSortBy: true,
      maxWidth: 140,
      cellRenderer: (cellValue: string) => parseRRule(cellValue).recurrence,
    },
    {
      id: 'schedule',
      Header: t('Schedule'),
      accessor: 'rRule',
      disableSortBy: true,
      maxWidth: 240,
      cellRenderer: (cellValue: string) => parseRRule(cellValue).schedule,
    },
    {
      id: 'hours',
      Header: t('Hours'),
      accessor: 'rRule',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (cellValue: string) => parseRRule(cellValue).hours,
    },
    {
      id: 'services',
      Header: t('Services'),
      accessor: 'bookableAppointmentTypes',
      disableSortBy: true,
      minWidth: 200,
      maxWidth: 240,
      cellRenderer: (cellValue: SDBookableAppointmentType[]) => {
        const text = cellValue?.map(({ name }) => name).join(', ');
        return <Truncated>{t(`{{text}}`, { text })}</Truncated>;
      },
    },
    {
      id: 'operatory',
      Header: t('Operatory'),
      accessor: 'bookableEntities',
      disableSortBy: true,
      maxWidth: 200,
      omit: availabilityMode !== 'OPERATORY',
      cellRenderer: (cellValue) => {
        const operatory = cellValue?.find((entity: BookableEntity) => entity.resourceType === 'OPERATORY');
        if (operatory) {
          return <Truncated>{operatory.resourceDisplayName}</Truncated>;
        }
        return '-';
      },
    },
    {
      id: 'primaryProvider',
      Header: t('Primary Provider'),
      accessor: 'bookableEntities',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (cellValue) => {
        const provider = cellValue?.find((entity: BookableEntity) => entity.resourceType === 'PROVIDER');
        if (provider) {
          return <Truncated>{provider.resourceDisplayName}</Truncated>;
        }
        return '-';
      },
    },
    {
      id: 'patientBooking',
      Header: t('Patient Booking'),
      accessor: 'bookingStrategy',
      disableSortBy: true,
      maxWidth: 170,
      cellRenderer: (cellValue: string) => {
        switch (cellValue) {
          case 'DIRECT':
            return (
              <Chip leftElement={<Icon name='check-small' />} variant='success' css={{ minWidth: 'fit-content' }}>
                {t('Self Bookable')}
              </Chip>
            );
          case 'REQUEST':
            return (
              <Chip leftElement={<Icon name='help-small' />} variant='indigo' css={{ minWidth: 'fit-content' }}>
                {t('Request Only')}
              </Chip>
            );
          case 'INTERNAL':
            return (
              <Chip leftElement={<Icon name='x-small' />} variant='critical' css={{ minWidth: 'fit-content' }}>
                {t('Not Available')}
              </Chip>
            );
          default:
            return '-';
        }
      },
    },
    {
      id: 'startDate',
      Header: t('Start Date'),
      accessor: 'rRule',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (cellValue: string) => parseRRule(cellValue).startDate,
    },
    {
      id: 'endDate',
      Header: t('End Date'),
      accessor: 'rRule',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (cellValue: string) => parseRRule(cellValue).endDate,
    },
  ];

  const handleDelete = () => {
    // TODO: Implement delete API call
    setTempScheduleDefinition(null);
  };

  return (
    <>
      <Table
        data={data}
        colConfig={colConfig}
        tableStyle={tableStyle}
        emptyStateConfig={{
          type: 'missing_forms' as const,
          header: t('No Availability Configured'),
          description:
            availabilityMode === 'OPERATORY'
              ? t(
                  'Define when this operatory is open for appointments. Create weekly schedules for regular hours or add one-time blocks for special dates.'
                )
              : t(
                  'Define when this provider is available for appointments. Create weekly schedules for regular hours or add one-time blocks for special dates.'
                ),
          action: { label: t('Create Schedule'), onClick: () => openAddEditTray(null, entityId, false) },
        }}
        rowActions={{
          actions: [
            {
              label: t('Edit'),
              Icon: EditIcon,
              onClick: (rowData) => {
                openAddEditTray(rowData, entityId, false);
              },
            },
            {
              label: t('Duplicate'),
              Icon: CopyIcon,
              onClick: (rowData) => {
                setTempScheduleDefinition(rowData);
                duplicateTriggerProps.onClick();
              },
            },
            {
              label: t('Delete'),
              Icon: TrashIcon,
              onClick: (rowData) => {
                setTempScheduleDefinition(rowData);
                deleteTriggerProps.onClick();
              },
            },
          ],
        }}
      />
      <DeleteConfirmationModal
        modalProps={deleteModalProps}
        availabilityRule={selectedScheduleDefinition}
        onConfirm={handleDelete}
      />
      <DuplicateConfirmationModal
        resourceOptions={resourceOptions}
        modalProps={duplicateModalProps}
        onConfirm={() => {
          openAddEditTray(tempScheduleDefinition, entityId, true);
          setTempScheduleDefinition(null);
        }}
      />
    </>
  );
};

const tableStyle = css({
  borderRadius: '0 !important',
  borderTop: 'none !important',
  borderLeft: 'none !important',
  borderRight: 'none !important',
});
